import { DocumentStatus, DocumentType } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Fragment, useState } from 'react';
import { PoolButton, PoolSplitButton } from '@synergy/components-next';
import { Tooltip } from '@mui/material';
import { usePaloShortcut } from '@synergy/components-palo';

import { PaloCompleteDocumentDialog } from '../../../../common/components/palo/palo-complete-document-dialog';
import { completeDemand, scheduleDemand, startDemand } from './store/demand-detail-actions';
import {
    demandDetailIsScheduledSelector,
    demandDetailStartDateSelector,
} from './store/overview/demand-detail-overview-selectors';
import { demandDetailPositionsBaselineSelector } from './store/positions/demand-detail-positions-selectors';
import {
    paloDocumentPhase,
    usePaloFlowButtonProps,
    usePaloIsMainAction,
} from '../../../detail-header/palo-main-action';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/**
 * The demand's own next step: start it, or end it.
 *
 * It used to double as the wizard's "Speichern & Weiter" and changed with the
 * step one stood on. The steps are tabs now, so the button says the same thing
 * wherever one is, and saving is the one filled button beside it (#2207).
 */
export const DemandDetailCtaButton = () => {
    const startDate = useAppSelector(demandDetailStartDateSelector);
    const isScheduled = useAppSelector(demandDetailIsScheduledSelector);

    const dispatch = useThunkDispatch();
    const status = useAppSelector(state => state.demandDetail.overview.stepData.values.status);
    const demandId = useAppSelector(state => state.demandDetail.overview.stepData.values.id);
    const unlocked = useAppSelector(state => state.demandDetail.common.unlocked);
    const [showFinishWarning, setShowFinishWarning] = useState(false);
    // Read from what is saved, not from the form: the start goes to the server,
    // which checks the same (#2253).
    const savedPositions = useAppSelector(demandDetailPositionsBaselineSelector);
    const missingToStart = [
        !savedPositions?.demandItems?.length && 'app.demands.detail.start.missingPositions',
        !(savedPositions?.deliveryDateFrom && savedPositions?.deliveryDateTo) &&
            'app.demands.detail.start.missingDeliveryPeriod',
    ].filter((reason): reason is string => !!reason);

    const completed =
        status === DocumentStatus.Canceled ||
        status === DocumentStatus.Completed ||
        status === DocumentStatus.CompletedWithTransition ||
        status === DocumentStatus.CompletedWithoutTransition;

    // A demand has no counterpart to cancelling, so there is no R.
    usePaloShortcut({
        combo: 'f',
        group: 'Beleg',
        label: 'Beenden',
        disabled: DocumentStatus.Ongoing !== status,
        handler: () => setShowFinishWarning(true),
    });

    // Starting and ending are what move the demand on, so they are the filled
    // action once there is nothing left to save (FRM-18).
    const phase = paloDocumentPhase(status, { saved: !!demandId, unlocked });
    const appearance = usePaloFlowButtonProps(phase, 'advance');
    const isMainAction = usePaloIsMainAction(phase, 'advance');

    const startItems =
        status === DocumentStatus.Created
            ? [
                  ...(!isScheduled
                      ? [
                            {
                                label: (
                                    <FormattedMessage
                                        id="app.demands.step.startDemand"
                                        values={{
                                            date: (
                                                <FormattedDate
                                                    value={startDate}
                                                    year="numeric"
                                                    month="2-digit"
                                                    day="2-digit"
                                                />
                                            ),
                                        }}
                                    />
                                ),
                                displayLabel: (
                                    <FormattedMessage
                                        id="app.demands.step.startDemand"
                                        values={{
                                            date: (
                                                <FormattedDate
                                                    value={startDate}
                                                    year="numeric"
                                                    month="2-digit"
                                                    day="2-digit"
                                                />
                                            ),
                                        }}
                                    />
                                ),
                                onClick: () => {
                                    dispatch(scheduleDemand());
                                },
                            },
                        ]
                      : []),
                  {
                      label: <FormattedMessage id="app.demands.step.startDemandImmediately" />,
                      displayLabel: <FormattedMessage id="app.demands.step.startDemandImmediately" />,
                      onClick: () => {
                          dispatch(startDemand());
                      },
                  },
              ]
            : status === DocumentStatus.Scheduled
            ? [
                  {
                      label: <FormattedMessage id="app.demands.step.startDemandImmediately" />,
                      displayLabel: <FormattedMessage id="app.demands.step.startDemandImmediately" />,
                      onClick: () => {
                          dispatch(startDemand());
                      },
                  },
              ]
            : [];

    /**
     * The split only earns its arrow where there is a choice. A demand whose
     * date is already set offers nothing but "start now", and the menu then
     * repeated the word on the button (06.10.2026).
     */
    const startButton =
        startItems.length > 1 ? (
            <PoolSplitButton
                {...appearance}
                menuItems={startItems}
                disabled={missingToStart.length > 0}
                sx={{ flexShrink: 0 }}
            />
        ) : (
            <PoolButton
                {...appearance}
                size="small"
                onClick={startItems[0]?.onClick}
                disabled={missingToStart.length > 0}
                sx={{ flexShrink: 0 }}
            >
                {startItems[0]?.displayLabel}
            </PoolButton>
        );

    /**
     * A draft may lack positions and delivery period; starting it may not. The
     * locked button says what is missing instead of just being grey.
     */
    const lockedStart =
        missingToStart.length > 0 ? (
            <Tooltip
                title={
                    <FormattedMessage
                        id="app.demands.detail.start.missing"
                        values={{
                            missing: missingToStart.map((id, index) => (
                                <Fragment key={id}>
                                    {index > 0 && ', '}
                                    <FormattedMessage id={id} />
                                </Fragment>
                            )),
                        }}
                    />
                }
            >
                {/* A disabled button fires no events; the span carries the hover. */}
                <span>{startButton}</span>
            </Tooltip>
        ) : (
            startButton
        );

    const button = [DocumentStatus.Created, DocumentStatus.Scheduled].includes(status) ? (
        lockedStart
    ) : (
        <PoolButton {...appearance} size="small" onClick={() => setShowFinishWarning(true)} disabled={completed}>
            <FormattedMessage id="app.demands.step.finishDemand" />
        </PoolButton>
    );

    // Only the step that is due is drawn. On a demand that is over, ending it
    // was a switched-off button beside the follow-up (#2206 rule 3).
    return (
        <>
            {isMainAction && button}
            <PaloCompleteDocumentDialog
                open={showFinishWarning}
                onClose={() => setShowFinishWarning(false)}
                onConfirm={() => {
                    setShowFinishWarning(false);
                    dispatch(completeDemand());
                }}
                titleId="app.demands.step.finishDemand"
                contentMessageId="app.demands.detail.finishWarning"
                confirmId="app.common.finish"
                documentType={DocumentType.Demand}
                documentId={demandId}
            />
        </>
    );
};
